import { BASE_GROUND_OFFSET } from "../../config";
import { BLOCK, type BlockKind, type VoxelWorld } from "../../types";
import type { StructureFootprint, StructureSpot } from "./types";

export type SeededRandom = () => number;

export function createSeededRandom(seed: number): SeededRandom {
    return function () {
        let t = (seed += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

export function footprintSize(footprint: StructureFootprint): { width: number; depth: number } {
    return typeof footprint === "number"
        ? { width: footprint, depth: footprint }
        : footprint;
}

export function findFlatStructureSpot(
    world: VoxelWorld,
    heights: number[][],
    surfaces: readonly BlockKind[],
    footprint: StructureFootprint,
    rng: SeededRandom,
    acceptsCell: (x: number, z: number) => boolean = () => true,
): StructureSpot | null {
    const { width, depth } = footprintSize(footprint);
    const margin = Math.max(width, depth) + 1;
    const size = world.size;

    for (let attempt = 0; attempt < 200; attempt++) {
        const x = margin + Math.floor(rng() * (size - margin * 2));
        const z = margin + Math.floor(rng() * (size - margin * 2));
        const topAbs = BASE_GROUND_OFFSET + heights[x][z];
        let valid = true;

        for (let dx = 0; dx < width && valid; dx++) {
            for (let dz = 0; dz < depth && valid; dz++) {
                const cellX = x + dx;
                const cellZ = z + dz;
                const cellTop = BASE_GROUND_OFFSET + heights[cellX][cellZ];
                const surface = world.get(cellX, topAbs, cellZ);
                const above = world.get(cellX, topAbs + 1, cellZ);
                valid =
                    acceptsCell(cellX, cellZ) &&
                    cellTop === topAbs &&
                    surface !== BLOCK.Empty &&
                    surfaces.includes(surface) &&
                    (!above || above === BLOCK.SnowBlock);
            }
        }

        if (valid) return { x, z, topAbs };
    }

    return null;
}

export function placeBlock(
    world: VoxelWorld,
    x: number,
    z: number,
    y: number,
    kind: BlockKind,
): void {
    world.set(x, y, z, kind);
}
